// helpers/piRats.js

/* =========================
   Helpers: rationals (multiples of pi)
   ========================= */

export function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

export function normRat(num, den) {
  if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
  const s = den < 0 ? -1 : 1;
  let n = num * s;
  let d = Math.abs(den);
  const g = gcd(n, d);
  n /= g;
  d /= g;
  if (n === 0) return { num: 0, den: 1 };
  return { num: n, den: d };
}

export function ratEq(a, b) {
  if (!a || !b) return false;
  const A = normRat(a.num, a.den);
  const B = normRat(b.num, b.den);
  return A.num === B.num && A.den === B.den;
}

export function uniqueRats(arr) {
  const out = [];
  for (const r of arr) if (!out.some((u) => ratEq(u, r))) out.push(r);
  return out;
}

export function ratToLatexPi(r) {
  const R = normRat(r.num, r.den);
  const n = R.num;
  const d = R.den;

  if (n === 0) return "0";
  const sign = n < 0 ? "-" : "";
  const an = Math.abs(n);

  if (d === 1) {
    if (an === 1) return `${sign}\\pi`;
    return `${sign}${an}\\pi`;
  }
  if (an === 1) return `${sign}\\dfrac{\\pi}{${d}}`;
  return `${sign}\\dfrac{${an}\\pi}{${d}}`;
}

export function safeStr(v) {
  if (v && typeof v === "object" && "raw" in v) return String(v.raw ?? "");
  return String(v ?? "");
}

/* =========================
   Parse user input as multiple of pi
   - accepts: 0, pi, -pi, pi/6, 3pi/4, -3pi/4, 2*pi/3, etc.
   ========================= */
export function parsePiMultiple(raw) {
  let s = String(raw ?? "").trim();
  if (!s) return null;

  s = s
    .replace(/[−–—]/g, "-")
    .replace(/\s+/g, "")
    .replace(/\\pi/g, "pi")
    .replace(/π/g, "pi")
    .toLowerCase();

  if (s === "0") return { num: 0, den: 1 };
  if (s === "pi") return { num: 1, den: 1 };
  if (s === "-pi") return { num: -1, den: 1 };

  const kp = s.match(/^([+\-]?\d+)\*?pi$/);
  if (kp) return normRat(parseInt(kp[1], 10), 1);

  const pOver = s.match(/^([+\-]?)pi\/(\d+)$/);
  if (pOver) {
    const sign = pOver[1] === "-" ? -1 : 1;
    const d = parseInt(pOver[2], 10);
    return normRat(sign, d);
  }

  const kpOver = s.match(/^([+\-]?\d+)\*?pi\/(\d+)$/);
  if (kpOver) {
    const k = parseInt(kpOver[1], 10);
    const d = parseInt(kpOver[2], 10);
    return normRat(k, d);
  }

  const pTimesInt = s.match(/^pi\*([+\-]?\d+)$/);
  if (pTimesInt) return normRat(parseInt(pTimesInt[1], 10), 1);

  return null;
}
